/**
 * TourContext - Manages React Joyride tour state
 * Provides methods to start, end, and skip tours across the app
 * Separate tour tracking for AMS and FMA apps
 */
import React, { createContext, useContext, useState, useCallback } from "react";

export type TourId = 
  | "ams-main" 
  | "ams-agents" 
  | "ams-agencies" 
  | "fma-home"
  | "fma-agencies"
  | "fma-training"
  | "fma-account";

export interface TourContextType {
  currentTour: TourId | null;
  isRunning: boolean;
  stepIndex: number;
  startTour: (tourId: TourId) => void;
  endTour: () => void;
  skipTour: () => void;
  setStepIndex: (index: number) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [currentTour, setCurrentTour] = useState<TourId | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const startTour = useCallback((tourId: TourId) => {
    setCurrentTour(tourId);
    setIsRunning(true);
    setStepIndex(0);
  }, []);

  const endTour = useCallback(() => {
    setCurrentTour(null);
    setIsRunning(false);
    setStepIndex(0);
  }, []);

  const skipTour = useCallback(() => {
    setCurrentTour(null);
    setIsRunning(false);
    setStepIndex(0);
  }, []);

  return (
    <TourContext.Provider
      value={{
        currentTour,
        isRunning,
        stepIndex,
        startTour,
        endTour,
        skipTour,
        setStepIndex,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
}
